import { createWebHistory, createWebHashHistory, createRouter } from "vue-router";
import { DEMO_MODE } from "@demo/config";
import { onAuthStateChange } from "./firebase/auth";
import { usePatientStore } from "./components/composables/usePatientStore.js";

// Where account creation actually happens — signup is retired in Retroact.
// Overridable so a staging build can point at a staging portal.
const PRAXIO_URL = import.meta.env.VITE_PRAXIO_URL || "https://praxio.net";

// Track authentication state
let currentUser = null;
let userRole = null;
let userIsSiteAdmin = false;
let authReady = false;

// Promise that resolves when auth state is initialized
const authReadyPromise = new Promise((resolve) => {
  onAuthStateChange(async (user) => {
    currentUser = user;
    
    // Get user role from token claims
    if (user) {
      try {
        const idTokenResult = await user.getIdTokenResult();
        userRole = idTokenResult.claims.role || null; // null = account owner
        userIsSiteAdmin = idTokenResult.claims.siteadmin === true;
      } catch (error) {
        console.error('Error getting user role:', error);
        userRole = null;
        userIsSiteAdmin = false;
      }
    } else {
      userRole = null;
      userIsSiteAdmin = false;
    }
    
    if (!authReady) {
      authReady = true;
      resolve(user);
    }
  });
});

/**
 * Role-based access control helper
 * @param {string|null} userRole - User's role (null = account owner, 'admin', 'editor', 'viewer')
 * @param {string|string[]} requiredRoles - Required role(s) for access
 * @returns {boolean} - Whether user has access
 */
const hasRequiredRole = (userRole, requiredRoles) => {
  if (!requiredRoles) return true; // No role requirement
  
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  
  // Account owner (null role) has access to everything
  if (userRole === null) return true;
  
  // Check if user's role is in the required roles
  return roles.includes(userRole);
};

/**
 * Check if user has permission to write/edit
 * @param {string|null} userRole - User's role
 * @returns {boolean}
 */
const canWrite = (userRole) => {
  return userRole === null || userRole === 'admin' || userRole === 'editor';
};

/**
 * Check if user is account owner
 * @param {string|null} userRole - User's role
 * @returns {boolean}
 */
const isAccountOwner = (userRole) => {
  return userRole === null;
};

/**
 * Route Meta Options:
 * 
 * - requiresAuth: boolean
 *   Route requires user to be authenticated
 * 
 * - requiresGuest: boolean
 *   Route is only for non-authenticated users (signin, signup)
 * 
 * - requiresAccountOwner: boolean
 *   Route is only accessible to account owners (no role)
 *   Invited users (admin, editor, viewer) will be redirected
 * 
 * - requiredRoles: string | string[]
 *   Route requires specific role(s): 'admin', 'editor', 'viewer'
 *   Account owners (null role) always have access
 *   Example: requiredRoles: ['admin', 'editor'] // viewers blocked
 * 
 * - redirectTo: string
 *   Path to redirect to when authorization fails
 *   Default: '/appointments'
 * 
 * Role Hierarchy:
 * - Account Owner (null): Full access to everything
 * - Admin: Can manage users, edit data
 * - Editor: Can edit data, cannot manage users
 * - Viewer: Read-only access
 */

const routes =  [
  {
    path: "/",
    redirect: "/appointments"
  },
  {
    path: "/signin",
    name: "signin",
    component: () => import("./components/auth/SignIn.vue"),
    meta: { requiresGuest: true }
  },
  {
    // Signup is RETIRED here — accounts are created on praxio.net only.
    // A guard rather than a `redirect`, because the destination is a different
    // origin and Vue Router can only redirect to routes inside this app.
    // `replace` so the back button doesn't bounce the user straight back here.
    path: "/signup",
    name: "signup",
    component: () => import("./components/auth/SignIn.vue"),
    meta: { requiresGuest: true },
    beforeEnter() {
      window.location.replace(`${PRAXIO_URL}/signup`);
    }
  },
  {
    // Function form, not the string `redirect: "/signin"`: a string redirect
    // DROPS the query string, which would throw away the `redirect` param the
    // auth guard just attached and reintroduce the lost-deep-link bug.
    path: "/login",
    redirect: (to) => ({ path: "/signin", query: to.query })
  },
  {
    path: "/auth/action",
    name: "authAction",
    component: () => import("./views/AuthAction.vue"),
    meta: { } // No auth requirement - works for both authenticated and unauthenticated users
  },
  {
    path: "/auth/magic-link",
    name: "magicLink",
    component: () => import("./components/auth/MagicLinkAuth.vue"),
    meta: { requiresGuest: true }
  },
  {
    path: "/auth/sso",
    name: "ssoAuth",
    component: () => import("./components/auth/SSOAuth.vue"),
    meta: { } // No auth requirement - works for anyone with a valid token
  },
  {
    path: "/sso-logout",
    name: "ssoLogout",
    component: () => import("./views/SSOLogout.vue"),
    meta: { } // No auth requirement - accessed via iframe from praxio
  },
  {
    // The deployment transfer, driven from the portal in a hidden iframe.
    //
    // NO AUTH REQUIREMENT, and that is not a hole. The guard exists to send an
    // unauthenticated visitor to a sign-in screen, which is precisely the wrong
    // thing to do inside a 1x1 frame: the redirect is invisible, the portal
    // waits forever, and the records never move. The route answers "no_session"
    // instead, and everything it can actually DO is gated where it belongs --
    // every endpoint the transfer touches is authenticated server-side, and the
    // frame refuses any message not from the portal's own origin.
    path: "/embed/deployment-transfer",
    name: "deploymentTransferFrame",
    component: () => import("./views/DeploymentTransferFrame.vue"),
    meta: { }
  },
  {
    // Retroact Connect: a third-party pharmacy system launching us in context.
    // The assertion arrives in the URL FRAGMENT (#token=...), never the query
    // string — see ConnectLaunch.vue for why. No auth requirement: the token
    // in the fragment is what establishes the session.
    path: "/launch",
    name: "connectLaunch",
    component: () => import("./components/auth/ConnectLaunch.vue"),
    meta: { }
  },
  {
    path: "/home",
    name: "home",
    component: () => import("./components/home.vue"),
    meta: { requiresAuth: true }
  },
  {
    // Quick entry — the one route with a phone layout.
    //
    // Retroact is a desktop application and stays one: the dosing calendar, the
    // results grid and the printed sheet have no usable small-screen form. What
    // does travel is recording a value away from the workstation — at the
    // counter after a capillary test, or when the lab calls and the clinician is
    // between rooms. This route captures the value and nothing else; the dose is
    // set at the desk, and what is waiting there is surfaced by the app bar's
    // pending-doses button (see composables/usePendingDoses.js).
    //
    // Reached from the Praxio portal's mobile menu and dashboard, which sign the
    // user in and land them here via /auth/sso?...&returnUrl=/quick-entry.
    //
    // requiredRoles excludes viewers: this is a write screen, and a viewer
    // reaching a form they cannot submit is worse than not offering it.
    path: "/quick-entry",
    name: "quickEntry",
    component: () => import("./components/quickEntry/QuickEntryView.vue"),
    meta: { requiresAuth: true, requiredRoles: ['admin', 'editor'] }
  },
  {
    path: "/patients",
    redirect: "/account/patients"
  },
  {
    path: "/results/:id",
    name: "results",
    component: () => import("./components/results/resultsDashboard.vue"),
    props: true,
    meta: { requiresAuth: true },
    redirect: { name: 'resultsCalendar' },
    children: [
      {
        path: "calendar",
        name: "resultsCalendar",
        component: () => import("./components/results/resultList/CalendarRoute.vue"),
        meta: { requiresAuth: true }
      },
      {
        path: "prescription",
        name: "resultsPrescription",
        component: () => import("./components/results/resultList/PrescriptionRoute.vue"),
        meta: { requiresAuth: true }
      },
      {
        path: "add-test",
        name: "addTest",
        component: () => import("./components/results/resultList/AddTestRoute.vue"),
        meta: { requiresAuth: true }
      },
      {
        path: "recurrent-doses",
        name: "recurrentDoses",
        component: () => import("./components/results/resultList/RecurrentDosesRoute.vue"),
        meta: { requiresAuth: true }
      },
      {
        path: "form",
        name: "resultForm",
        component: () => import("./components/results/resultList/FormRoute.vue"),
        meta: { requiresAuth: true }
      }
    ]
  },
  {
    path: "/drugsList",
    name: "drugsList",
    component: () => import("./components/drugs/drugsList.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/addDrug",
    name: "addDrug",
    component: () => import("./components/drugs/addDrug.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/patient-report/:id",
    name: "patientReport",
    component: () => import("./components/reports/patientReport.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/account",
    name: "account",
    component: () => import("./components/account/UserAccount.vue"),
    meta: { requiresAuth: true },
    redirect: "/account/patients",
    children: [
      {
        path: "patients",
        name: "accountPatients",
        component: () => import("./components/account/PatientsSettings.vue"),
        meta: { requiresAuth: true }
      },
      {
        path: "users",
        name: "accountUsers",
        component: () => import("./components/account/UserManagement.vue"),
        meta: { requiresAuth: true, requiresAccountOwner: true },
        redirect: "/account/users/list",
        children: [
          {
            path: "add",
            name: "accountUsersAdd",
            component: () => import("./components/account/AddUserForm.vue"),
            meta: { requiresAuth: true, requiresAccountOwner: true }
          },
          {
            path: "list",
            name: "accountUsersList",
            component: () => import("./components/account/UsersList.vue"),
            meta: { requiresAuth: true, requiresAccountOwner: true }
          }
        ]
      },
      {
        path: "settings",
        name: "accountSettings",
        component: () => import("./components/account/AccountSettings.vue"),
        meta: { requiresAuth: true },
        redirect: "/account/settings/clinical",
        children: [
          {
            path: "credentials",
            name: "accountCredentials",
            component: () => import("./components/account/ProfileSettings.vue"),
            meta: { requiresAuth: true }
          },
          {
            path: "organization",
            name: "accountOrganization",
            component: () => import("./components/account/OrganizationSettings.vue"),
            meta: { requiresAuth: true }
          },
          {
            // Retroact Connect: which pharmacy systems may launch this account.
            // Owner-only, because enabling one widens who can reach clinical
            // data. The server enforces this again on every write — see
            // connect.controller.js.
            path: "integrations",
            name: "accountIntegrations",
            component: () => import("./components/account/IntegrationSettings.vue"),
            meta: { requiresAuth: true, requiresAccountOwner: true }
          },
          {
            // Clinical protocol selection. Lives in the clinical app rather than
            // the Praxio portal because it governs what this app refuses to
            // record, and because the pharmacist choosing it is working here.
            path: "clinical",
            name: "accountClinical",
            component: () => import("./components/account/ClinicalSettings.vue"),
            meta: { requiresAuth: true }
          },
          {
            path: "billing",
            name: "accountBilling",
            component: () => import("./components/subscription/SubscriptionManager.vue"),
            meta: { 
              requiresAuth: true, 
              requiresAccountOwner: true,
              redirectTo: '/appointments'
            }
          }
        ]
      },
      {
        path: "backups",
        name: "accountBackups",
        component: () => import("./components/account/BackupSettings.vue"),
        meta: { 
          requiresAuth: true, 
          requiresAccountOwner: true,
          redirectTo: '/appointments' // Redirect non-account owners to appointments
        },
        redirect: "/account/backups/backup",
        children: [
          {
            path: "backup",
            name: "accountBackup",
            component: () => import("./components/account/BackupLanding.vue"),
            meta: { 
              requiresAuth: true, 
              requiresAccountOwner: true,
              redirectTo: '/appointments'
            }
          },
          {
            path: "restore",
            name: "accountRestore",
            component: () => import("./components/account/RestoreLanding.vue"),
            meta: { 
              requiresAuth: true, 
              requiresAccountOwner: true,
              redirectTo: '/appointments'
            }
          },
          {
            // Moving the panel between the two deployments. Owner-only like its
            // siblings, and for the same reason the portal's switch is: this
            // decides where an entire pharmacy's clinical records live.
            //
            // The screen renders an "unavailable" state rather than 404ing in a
            // cloud build, because the portal links here by path and a dead link
            // in the middle of a data migration is worse than a screen that
            // explains itself.
            path: "transfer",
            name: "accountTransfer",
            component: () => import("./components/account/TransferLanding.vue"),
            meta: {
              requiresAuth: true,
              requiresAccountOwner: true,
              redirectTo: '/appointments'
            }
          }
        ]
      },
      {
        path: "admin",
        name: "accountAdmin",
        component: () => import("./components/account/AdminSettings.vue"),
        meta: { 
          requiresAuth: true, 
          requiresSiteAdmin: true,
          redirectTo: '/appointments'
        },
        redirect: "/account/admin/users",
        children: [
          {
            path: "users",
            name: "accountAdminUsers",
            component: () => import("./components/account/admin/UsersAdmin.vue"),
            meta: { 
              requiresAuth: true, 
              requiresSiteAdmin: true,
              redirectTo: '/appointments'
            }
          },
          {
            path: "online",
            name: "accountAdminOnline",
            component: () => import("./components/account/admin/OnlineUsers.vue"),
            meta: { 
              requiresAuth: true, 
              requiresSiteAdmin: true,
              redirectTo: '/appointments'
            }
          },
          {
            path: "accounts",
            name: "accountAdminAccounts",
            component: () => import("./components/account/admin/AccountControl.vue"),
            meta: { 
              requiresAuth: true, 
              requiresSiteAdmin: true,
              redirectTo: '/appointments'
            }
          },
          {
            path: "trials",
            name: "accountAdminTrials",
            component: () => import("./components/account/admin/TrialManagement.vue"),
            meta: { 
              requiresAuth: true, 
              requiresSiteAdmin: true,
              redirectTo: '/appointments'
            }
          },
          {
            path: "impersonate",
            name: "accountAdminImpersonate",
            component: () => import("./components/account/admin/Impersonate.vue"),
            meta: { 
              requiresAuth: true, 
              requiresSiteAdmin: true,
              redirectTo: '/appointments'
            }
          }
        ]
      },
      {
        path: "profile",
        redirect: "/account/settings/credentials"
      }
    ]
  },
  // NO /plans ROUTE. Retroact does not sell anything: praxio.net owns purchase,
  // and the one upgrade route in this app is PatientLimitDialog.vue's link to
  // the portal's plan page. `PlanComparison.vue` was deleted on 2026-08-27 —
  // it posted a client-supplied Stripe price id to this app's own checkout
  // endpoint (also removed), and it was still selling the RETIRED flat-rate
  // plans at $45 / $35, which have not existed since the panel-size ladder
  // replaced them.
  //
  // The plan-selection gate that used to trap account owners here — App.vue
  // pushed anyone with no `planType` to /plans and a watcher shoved them back
  // if they navigated away — went with it. Free is a plan now, so that gate
  // caught every new account and held it on a page selling prices we no longer
  // charge.
  //
  // NO /trial-checkout ROUTE either. TrialCheckout.vue was the last screen that
  // could start a 14-day trial; it and the two endpoints behind it went on
  // 2026-08-28 when the trial was retired in favour of the free tier.
  {
    path: "/userAccount",
    redirect: "/account"
  },
  {
    path: "/appointments",
    name: "appointments",
    component: () => import("./components/appointments/appointmentsView.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/trends/:id",
    name: "trends",
    component: () => import("./components/trends/TrendsView.vue"),
    props: true,
    meta: { requiresAuth: true },
    redirect: to => ({ name: 'trendsDataSheet', params: { id: to.params.id } })
  },
  {
    path: "/trends/:id/data-sheet",
    name: "trendsDataSheet",
    component: () => import("./components/trends/TrendsView.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/trends/:id/graphics",
    name: "trendsGraphics",
    component: () => import("./components/trends/TrendsView.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/trends/:id/reports",
    name: "trendsReports",
    component: () => import("./components/trends/TrendsView.vue"),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: "/trends",
    redirect: "/appointments"
  },
  {
    // The AI dose advisor was removed (2026-08); its URLs redirect so any
    // bookmark or stale link lands on a real screen rather than nothing.
    path: "/dose-advisor/:id",
    redirect: "/appointments"
  },
  {
    path: "/dose-advisor",
    redirect: "/appointments"
  },
  {
    path: "/tickets",
    name: "tickets",
    component: () => import("./components/tickets/TicketsView.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/results",
    component: () => import("./components/results/resultsDashboard.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/patient-report",
    component: () => import("./components/reports/patientReport.vue"),
    meta: { requiresAuth: true }
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/appointments"
  }
];

const router = createRouter({
  // The demo is served as plain static files from the portal (/demo-app/), which
  // has no SPA rewrite: a deep path would 404 on reload. Hash history keeps every
  // route inside one document, so the visitor can navigate — and reload — freely.
  history: DEMO_MODE ? createWebHashHistory('/demo-app/') : createWebHistory(),
  routes,
});

// Navigation guard for authentication and authorization
router.beforeEach(async (to, from, next) => {
  // The public demo is always signed in and can never sign in or out: reaching
  // /signin there sends the browser to the portal's SSO page, which inside the
  // demo frame means the marketing site loading a copy of itself. Send every
  // auth route back into the app instead.
  if (DEMO_MODE && (to.path.startsWith('/signin') || to.path.startsWith('/login') ||
      to.path.startsWith('/signup') || to.path.startsWith('/auth') || to.path.startsWith('/sso'))) {
    next('/appointments');
    return;
  }

  // Skip auth check for SSO routes - they handle their own auth
  if (to.path.startsWith('/auth/sso') || to.path.startsWith('/auth/magic-link')) {
    next();
    return;
  }

  // Wait for Firebase auth to initialize
  await authReadyPromise;

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);
  const requiresAccountOwner = to.matched.some(record => record.meta.requiresAccountOwner);
  const requiredRoles = to.matched.find(record => record.meta.requiredRoles)?.meta.requiredRoles;
  
  // 1. Check authentication
  if (requiresAuth && !currentUser) {
    // NOTE: the Praxio /api/sso/auto-login endpoint was removed (2026-07). It
    // depended on an auth cookie Praxio never sets. If cross-site auto-login is
    // reintroduced, build it on Firebase session cookies (httpOnly), never by
    // reflecting the ID token through a JS-readable cookie, and mint the SSO
    // JWT via Praxio's shared ssoToken util so role claims are always present.
    // Carry the intended destination through the SSO round trip. Without the
    // `redirect` query, SignIn.vue built its returnUrl from window.location.href
    // — which by that point is already /signin — so every deep link was silently
    // downgraded to the default landing page after sign-in. `to.fullPath` keeps
    // the path AND any query/hash the clinician actually followed.
    next({ path: '/login', query: { redirect: to.fullPath } });
    return;
  }
  
  if (requiresGuest && currentUser) {
    // Redirect to home if route is for guests only and user is logged in
    next('/');
    return;
  }
  
  // 2. Check role-based authorization (only for authenticated users)
  if (requiresAuth && currentUser) {
    // Check if route requires site admin
    const requiresSiteAdmin = to.matched.some(record => record.meta.requiresSiteAdmin);
    if (requiresSiteAdmin && !userIsSiteAdmin) {
      const redirectPath = to.meta.redirectTo || '/appointments';
      next(redirectPath);
      return;
    }
    
    // Check if route requires account owner
    if (requiresAccountOwner && !isAccountOwner(userRole)) {
      const redirectPath = to.meta.redirectTo || '/appointments';
      next(redirectPath);
      return;
    }
    
    // Check if route requires specific roles
    if (requiredRoles && !hasRequiredRole(userRole, requiredRoles)) {
      const redirectPath = to.meta.redirectTo || '/appointments';
      next(redirectPath);
      return;
    }
    
    // 3. Check if user has no patients and redirect to patients page
    const patientStore = usePatientStore();
    
    // Ensure patients are loaded before checking
    // Only fetch if the store is not enriched yet (first load or page reload)
    if (!patientStore.isEnriched.value) {
      try {
        await patientStore.fetchPatients();
      } catch (error) {
        console.error('Error fetching patients in router guard:', error);
      }
    }
    
    const hasPatients = patientStore.patients.value && patientStore.patients.value.length > 0;
    
    // Auto-select first active patient if none selected in localStorage (e.g., after SSO login)
    if (hasPatients && !patientStore.activeSelectedPatientId.value) {
      const firstActivePatient = patientStore.activePatients.value?.[0];
      if (firstActivePatient) {
        patientStore.activeSelectedPatientId.value = firstActivePatient.id;
        patientStore.selectedPatientId.value = firstActivePatient.id;
      } else {
        console.warn('⚠️ No active patients found to auto-select');
      }
    }
    
    // Redirect to patients page if no patients exist and not already going there or to settings
    if (!hasPatients && to.path !== '/patients' && to.path !== '/account/patients' && to.path !== '/userAccount' && !to.path.startsWith('/account')) {
      next('/account/patients');
      return;
    }
  }
  
  // All checks passed
  next();
});

export default router;